import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";
import { useSalon } from "../../../context/SalonContext";
import { useFeatureAccess } from "../../../context/FeatureAccessContext";
import CalendarView from "./index";

/**
 * Wrapper component for CalendarView. Uses /access for minimal routing checks.
 *
 * WHEN THIS COMPONENT IS SHOWN:
 * - Route: /admin/calendar (or /admin/* catch-all → Navigate to /admin/calendar)
 * - Default route: path="*" → LandingRedirect (compute from access)
 * - After AppContent gate: isAuthenticated, SalonContext loaded
 * - Inside AppLayout under /admin/*
 *
 * ROUTING LOGIC (direct navigation — /admin/calendar is always allowed per docs):
 * - Access loading → show LoadingOverlay (do not redirect)
 * - Access null and no error → show LoadingOverlay (wait for data)
 * - Access null and error (fetch failed) → redirect to /account-creation
 * - Access loaded: no salon → /account-creation
 * - Access loaded: Non-Owner → render calendar
 * - Access loaded: Owner + initial incomplete → /account-creation
 * - Access loaded: Owner + initial complete → render calendar (no redirect based on progressive)
 */
export default function CalendarViewWithOnboardingCheck() {
  const navigate = useNavigate();
  const { businessInfo, isMissingSalon } = useSalon();
  const {
    access,
    isLoading: isAccessLoading,
    error: accessError,
  } = useFeatureAccess();
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Wait for access data - do not redirect until we have data or know fetch failed
    if (isAccessLoading) {
      return;
    }

    // Access null: only redirect if we explicitly know the fetch failed
    if (!access) {
      if (accessError) {
        navigate("/account-creation", { replace: true });
      }
      return;
    }

    // We have access - make routing decisions
    // Case 1: No salon → new Owner → /account-creation
    if (isMissingSalon || (!businessInfo && isMissingSalon !== null)) {
      navigate("/account-creation", { replace: true });
      return;
    }

    // Case 2: Non-Owner users (Manager/Employee/Assistant) → render calendar (skip onboarding)
    if (
      access.user_level === "Manager" ||
      access.user_level === "Employee" ||
      access.user_level === "Assistant"
    ) {
      setShouldRender(true);
      return;
    }

    // Case 3: Owner - check initial onboarding only (direct nav: allow calendar regardless of progressive)
    const { onboarding } = access;

    if (
      onboarding?.initial_onboarding === "null" ||
      onboarding?.initial_onboarding === "inprogress"
    ) {
      navigate("/account-creation", { replace: true });
      return;
    }

    // Initial onboarding complete - allow calendar (no redirect based on progressive)
    setShouldRender(true);
  }, [
    isAccessLoading,
    access,
    accessError,
    businessInfo,
    isMissingSalon,
    navigate,
  ]);

  // Show loading overlay until we have access data or know fetch failed
  if (isAccessLoading || !access) {
    return <LoadingOverlay visible />;
  }

  // We have access but routing may have sent us elsewhere
  if (!shouldRender) {
    return <LoadingOverlay visible />;
  }

  return <CalendarView />;
}
